/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import BedRockContext from '../../BedRockContext'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../_metronic/layout/core'
import {Card, Row, Col, Badge, Spinner, Table, Button} from 'react-bootstrap'
import {getAllSyncStatus, genericGetAll} from '../generic/crud'
import {ENTITY_ID_CUSTOMER_SALE, ENTITY_ID_RAW_PURCHASE} from '../generic/Constants'
import {VehicleAddEditDialog} from '../vehicle/VehicleAddEditDialog'
import moment from 'moment'
import {formatDateTime} from '../../utils/Utils'
import {HardwareCheckButton} from '../../components/hardware-check/HardwareCheckButton'

// ============================================
// INTERFACES
// ============================================

interface QuickActionProps {
  title: string
  subtitle: string
  description: string[]
  icon: string
  color: string
  onClick: () => void
}

interface SyncData {
  entityCount?: number
  totalRecordCount?: number
  totalPendingCount?: number
  entities?: Array<{
    displayName: string
    pendingCount: number
    syncStatus: string
  }>
}

interface SaleItem {
  inventoryItemName?: string
  quantity?: number
  price?: number
  unitSymbol?: string
}

interface Sale {
  id: string
  ticketSequenceNumber?: string
  vehicleNumber?: string
  salesCustomer?: {
    name: string
  }
  itemsList?: SaleItem[]
  emptyWeight?: number
  loadedWeight?: number
  netWeight?: number
  salesFirstStepDate?: string
  salesSecondStepDate?: string
  isPartialRecord?: boolean
  grandTotal?: number
  synced?: boolean
}

interface PurchaseItem {
  inventoryItemName?: string
  quantity?: number
  price?: number
  unitSymbol?: string
}

interface Purchase {
  id: string
  ticketSequenceNumber?: string
  vehicleNumber?: string
  supplier?: {
    name: string
  }
  itemsList?: PurchaseItem[]
  emptyWeight?: number
  loadedWeight?: number
  netWeight?: number
  purchaseFirstStepDate?: string
  purchaseDate?: string
  purchaseType?: string
  isPartialRecord?: boolean
  source?: string
  synced?: boolean
}

interface TodayStats {
  salesCount: number
  purchasesCount: number
  salesTotalWeight: number
  salesTotalCft: number
  purchasesTotalWeight: number
  purchasesTotalCft: number
}

// ============================================
// LIVE CLOCK COMPONENT
// ============================================

const LiveClock: FC = () => {
  const [time, setTime] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  return (
    <Card className="bg-dark text-white mb-5" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* SVG Background - Clock gears pattern */}
      <div style={{ position: 'absolute', top: -20, right: -20, width: '200px', height: '200px', opacity: 0.08, pointerEvents: 'none' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="100" r="80" fill="none" stroke="#FFC700" strokeWidth="4" />
          <circle cx="100" cy="100" r="60" fill="none" stroke="#FFC700" strokeWidth="3" />
          <circle cx="100" cy="100" r="40" fill="none" stroke="#FFC700" strokeWidth="2" />
          <line x1="100" y1="100" x2="100" y2="40" stroke="#FFC700" strokeWidth="4" strokeLinecap="round" />
          <line x1="100" y1="100" x2="140" y2="100" stroke="#FFC700" strokeWidth="3" strokeLinecap="round" />
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(angle => (
            <circle key={angle} cx={100 + 70 * Math.cos(angle * Math.PI / 180)} cy={100 + 70 * Math.sin(angle * Math.PI / 180)} r="4" fill="#FFC700" />
          ))}
        </svg>
      </div>
      <div style={{ position: 'absolute', bottom: -30, left: -30, width: '120px', height: '120px', opacity: 0.06, pointerEvents: 'none' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFC700" d="M100,20 L108,40 L130,40 L112,54 L120,76 L100,62 L80,76 L88,54 L70,40 L92,40 Z" />
          <circle cx="100" cy="140" r="40" fill="none" stroke="#FFC700" strokeWidth="3" />
        </svg>
      </div>
      <Card.Body className="py-4" style={{ position: 'relative', zIndex: 1 }}>
        <Row className="align-items-center">
          <Col xs={12} md={6} className="text-center text-md-start mb-3 mb-md-0">
            <div className="d-flex align-items-center justify-content-center justify-content-md-start">
              <i className="fas fa-clock fs-2x me-3 text-warning"></i>
              <div>
                <h1 className="text-white fw-bold mb-0" style={{fontSize: '2.5rem', fontFamily: 'monospace'}}>
                  {moment(time).format('hh:mm:ss A')}
                </h1>
              </div>
            </div>
          </Col>
          <Col xs={12} md={6} className="text-center text-md-end">
            <span className="text-white opacity-75 fs-5">
              {moment(time).format('dddd, DD MMMM YYYY')}
            </span>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

// ============================================
// QUICK ACTION BUTTON COMPONENT
// ============================================

// Get color hex value from Bootstrap color name
const getColorHex = (color: string): string => {
  const colorMap: { [key: string]: string } = {
    info: '#009EF7',
    success: '#50CD89',
    warning: '#FFC700',
    primary: '#3E97FF',
    danger: '#F1416C'
  }
  return colorMap[color] || '#009EF7'
}

// Quick Action background patterns
const QuickActionPattern: FC<{ color: string; type: string }> = ({ color, type }) => {
  const hexColor = getColorHex(color)

  if (type === 'truck') {
    // Vehicle/truck pattern
    return (
      <>
        <div style={{ position: 'absolute', top: -10, right: -10, width: '140px', height: '140px', opacity: 0.06, pointerEvents: 'none' }}>
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="80" width="120" height="60" rx="8" fill={hexColor} />
            <rect x="140" y="100" width="50" height="40" rx="5" fill={hexColor} />
            <circle cx="50" cy="150" r="20" fill={hexColor} />
            <circle cx="120" cy="150" r="20" fill={hexColor} />
            <circle cx="165" cy="150" r="15" fill={hexColor} />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: -20, left: -20, width: '80px', height: '80px', opacity: 0.04, pointerEvents: 'none' }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="40" fill={hexColor} />
          </svg>
        </div>
      </>
    )
  } else if (type === 'bolt') {
    // Lightning bolt pattern
    return (
      <>
        <div style={{ position: 'absolute', top: -10, right: -10, width: '140px', height: '140px', opacity: 0.07, pointerEvents: 'none' }}>
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <polygon points="100,10 60,90 90,90 70,190 140,80 100,80 130,10" fill={hexColor} />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: 10, left: 10, width: '60px', height: '60px', opacity: 0.05, pointerEvents: 'none' }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <polygon points="50,5 30,45 45,45 35,95 70,40 50,40 65,5" fill={hexColor} />
          </svg>
        </div>
        <div style={{ position: 'absolute', top: 50, right: 80, width: '30px', height: '30px', opacity: 0.04, pointerEvents: 'none' }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="40" fill={hexColor} />
          </svg>
        </div>
      </>
    )
  } else if (type === 'cart') {
    // Shopping cart pattern
    return (
      <>
        <div style={{ position: 'absolute', top: -10, right: -10, width: '140px', height: '140px', opacity: 0.07, pointerEvents: 'none' }}>
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path d="M30,50 L50,50 L70,120 L150,120 L170,60 L60,60" fill="none" stroke={hexColor} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="80" cy="150" r="15" fill={hexColor} />
            <circle cx="140" cy="150" r="15" fill={hexColor} />
            <rect x="75" y="75" width="60" height="35" rx="5" fill={hexColor} opacity="0.5" />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: -15, left: -15, width: '80px', height: '80px', opacity: 0.04, pointerEvents: 'none' }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="20" width="60" height="60" rx="10" fill={hexColor} />
          </svg>
        </div>
      </>
    )
  } else {
    // Balance scale pattern
    return (
      <>
        <div style={{ position: 'absolute', top: -10, right: -10, width: '150px', height: '150px', opacity: 0.06, pointerEvents: 'none' }}>
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <line x1="100" y1="30" x2="100" y2="160" stroke={hexColor} strokeWidth="8" />
            <line x1="40" y1="60" x2="160" y2="60" stroke={hexColor} strokeWidth="6" />
            <polygon points="40,70 20,120 60,120" fill={hexColor} />
            <polygon points="160,70 140,120 180,120" fill={hexColor} />
            <rect x="80" y="150" width="40" height="20" rx="4" fill={hexColor} />
            <circle cx="100" cy="60" r="12" fill={hexColor} />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: -15, left: -15, width: '70px', height: '70px', opacity: 0.04, pointerEvents: 'none' }}>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="10" width="80" height="80" rx="10" fill={hexColor} />
          </svg>
        </div>
      </>
    )
  }
}

const QuickActionButton: FC<QuickActionProps> = ({title, subtitle, description, icon, color, onClick}) => {
  const getPatternType = () => {
    if (icon.includes('truck')) return 'truck'
    if (icon.includes('bolt')) return 'bolt'
    if (icon.includes('cart')) return 'cart'
    return 'scale'
  }

  return (
    <Card
      className={`h-100 border border-2 border-${color} border-hover cursor-pointer`}
      style={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onClick}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)'
        e.currentTarget.style.boxShadow = `0 10px 20px rgba(0,0,0,0.15)`
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {/* SVG Background Pattern */}
      <QuickActionPattern color={color} type={getPatternType()} />

      <Card.Body className="p-6" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header with icon and title */}
        <div className="d-flex align-items-center mb-4">
          <div className={`btn btn-icon btn-${color} btn-lg me-4`}>
            <i className={`${icon} fs-2 text-white`}></i>
          </div>
          <div>
            <h3 className={`fw-bold text-${color} mb-0`}>{title}</h3>
            <span className="text-muted fs-7">{subtitle}</span>
          </div>
        </div>

        {/* Description bullets */}
        <div className="ps-2">
          {description.map((item, index) => (
            <div key={index} className="d-flex align-items-start mb-2">
              <i className={`fas fa-check-circle text-${color} me-2 mt-1 fs-8`}></i>
              <span className="text-gray-700 fs-7">{item}</span>
            </div>
          ))}
        </div>

        {/* Click indicator */}
        <div className={`text-${color} fw-semibold mt-4 d-flex align-items-center`}>
          <span>Click to start</span>
          <i className="fas fa-arrow-right ms-2"></i>
        </div>
      </Card.Body>
    </Card>
  )
}

// ============================================
// QUICK ACTION GROUP CARD (title + 2 sub-actions)
// ============================================

interface SubAction {
  label: string
  hint: string
  icon: string
  onClick: () => void
}

interface QuickActionGroupProps {
  title: string
  subtitle: string
  icon: string
  color: string
  subActions: SubAction[]
}

const QuickActionGroupCard: FC<QuickActionGroupProps> = ({title, subtitle, icon, color, subActions}) => {
  const hex = getColorHex(color)

  return (
    <Card
      className={`h-100 border border-2 border-${color}`}
      style={{
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)'
        e.currentTarget.style.boxShadow = `0 10px 20px rgba(0,0,0,0.15)`
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {/* SVG Background watermark */}
      <div style={{ position: 'absolute', top: -25, right: -25, width: '160px', height: '160px', opacity: 0.07, pointerEvents: 'none' }}>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="38" fill={hex} />
        </svg>
      </div>

      <Card.Body className="p-5" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <div className={`btn btn-icon btn-${color} btn-lg me-3`}>
            <i className={`${icon} fs-2 text-white`}></i>
          </div>
          <div>
            <h3 className={`fw-bold text-${color} mb-0`}>{title}</h3>
            <span className="text-muted fs-7">{subtitle}</span>
          </div>
        </div>

        {/* Sub-action tiles */}
        <div className="d-flex flex-column gap-3">
          {subActions.map((action, index) => (
            <div
              key={index}
              className="d-flex align-items-center p-3 rounded cursor-pointer"
              style={{
                cursor: 'pointer',
                backgroundColor: `rgba(${parseInt(hex.slice(1,3),16)}, ${parseInt(hex.slice(3,5),16)}, ${parseInt(hex.slice(5,7),16)}, 0.08)`,
                border: `1px solid rgba(${parseInt(hex.slice(1,3),16)}, ${parseInt(hex.slice(3,5),16)}, ${parseInt(hex.slice(5,7),16)}, 0.2)`,
                transition: 'all 0.2s ease'
              }}
              onClick={action.onClick}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = `rgba(${parseInt(hex.slice(1,3),16)}, ${parseInt(hex.slice(3,5),16)}, ${parseInt(hex.slice(5,7),16)}, 0.18)`
                e.currentTarget.style.transform = 'translateX(4px)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = `rgba(${parseInt(hex.slice(1,3),16)}, ${parseInt(hex.slice(3,5),16)}, ${parseInt(hex.slice(5,7),16)}, 0.08)`
                e.currentTarget.style.transform = 'translateX(0)'
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: hex,
                  flexShrink: 0
                }}
              >
                <i className={`${action.icon} fs-5 text-white`}></i>
              </div>
              <div className="flex-grow-1">
                <div className={`fw-bold text-${color}`} style={{lineHeight: 1.1}}>{action.label}</div>
                <div className="text-muted fs-8">{action.hint}</div>
              </div>
              <i className={`fas fa-arrow-right text-${color} ms-2`}></i>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  )
}

// ============================================
// ANIMATED SUMMARY CARD COMPONENT
// ============================================

interface SummaryCardProps {
  title: string
  subtitle: string
  icon: string
  color: string
  accent: string
  count: number
  weight: number
  cft: number
  variant: 'sale' | 'purchase'
}

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const ANIMATION_STYLES = `
  @keyframes bedrock-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  @keyframes bedrock-drift { 0%,100% { transform: translateX(0); } 50% { transform: translateX(14px); } }
  @keyframes bedrock-pulse { 0%,100% { transform: scale(1); opacity: 0.2; } 50% { transform: scale(1.15); opacity: 0.32; } }
  @keyframes bedrock-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes bedrock-spin-rev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
`

const AnimatedBackground: FC<{ variant: 'sale' | 'purchase'; color: string; accent: string }> = ({ variant, color, accent }) => {
  if (variant === 'sale') {
    return (
      <>
        {/* Flowing waves along the bottom (SMIL-animated) */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', opacity: 0.18, pointerEvents: 'none', overflow: 'hidden' }}>
          <svg viewBox="0 0 400 200" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
            <path fill={color}>
              <animate attributeName="d" dur="8s" repeatCount="indefinite"
                values="M0,100 C80,150 120,50 200,100 C280,150 320,50 400,100 L400,200 L0,200 Z;
                        M0,110 C80,60 120,160 200,110 C280,60 320,160 400,110 L400,200 L0,200 Z;
                        M0,100 C80,150 120,50 200,100 C280,150 320,50 400,100 L400,200 L0,200 Z" />
            </path>
            <path fill={accent} opacity="0.5">
              <animate attributeName="d" dur="6s" repeatCount="indefinite"
                values="M0,130 C60,170 140,90 200,130 C260,170 340,90 400,130 L400,200 L0,200 Z;
                        M0,140 C60,100 140,180 200,140 C260,100 340,180 400,140 L400,200 L0,200 Z;
                        M0,130 C60,170 140,90 200,130 C260,170 340,90 400,130 L400,200 L0,200 Z" />
            </path>
          </svg>
        </div>
        {/* Pulsing concentric rings */}
        <div style={{ position: 'absolute', top: 20, right: 25, width: '100px', height: '100px', pointerEvents: 'none', animation: 'bedrock-pulse 3s ease-in-out infinite' }}>
          <svg viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="5" opacity="0.4" />
            <circle cx="50" cy="50" r="25" fill="none" stroke={accent} strokeWidth="4" opacity="0.45" />
          </svg>
        </div>
        {/* Floating coin */}
        <div style={{ position: 'absolute', top: 70, right: 140, width: '30px', height: '30px', pointerEvents: 'none', animation: 'bedrock-float 4s ease-in-out infinite' }}>
          <svg viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="38" fill={accent} opacity="0.55" />
            <circle cx="50" cy="50" r="22" fill="none" stroke="#fff" strokeWidth="3" opacity="0.6" />
          </svg>
        </div>
      </>
    )
  }

  // Purchase variant: rotating cog + drifting crates + pulsing dot
  return (
    <>
      {/* Rotating gear top-right */}
      <div style={{ position: 'absolute', top: -15, right: -15, width: '150px', height: '150px', opacity: 0.14, pointerEvents: 'none', animation: 'bedrock-spin 30s linear infinite' }}>
        <svg viewBox="0 0 100 100">
          <g fill={color}>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
              <rect key={a} x="46" y="4" width="8" height="16" rx="2" transform={`rotate(${a} 50 50)`} />
            ))}
            <circle cx="50" cy="50" r="32" />
          </g>
          <circle cx="50" cy="50" r="15" fill="#fff" />
        </svg>
      </div>
      {/* Counter-rotating smaller gear */}
      <div style={{ position: 'absolute', top: 60, right: 95, width: '55px', height: '55px', opacity: 0.1, pointerEvents: 'none', animation: 'bedrock-spin-rev 18s linear infinite' }}>
        <svg viewBox="0 0 100 100">
          <g fill={accent}>
            {[0, 60, 120, 180, 240, 300].map((a) => (
              <rect key={a} x="46" y="6" width="8" height="14" rx="2" transform={`rotate(${a} 50 50)`} />
            ))}
            <circle cx="50" cy="50" r="28" />
          </g>
          <circle cx="50" cy="50" r="12" fill="#fff" />
        </svg>
      </div>
      {/* Floating crate bottom-left */}
      <div style={{ position: 'absolute', bottom: 20, left: 18, width: '70px', height: '70px', opacity: 0.22, pointerEvents: 'none', animation: 'bedrock-float 5s ease-in-out infinite' }}>
        <svg viewBox="0 0 100 100">
          <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill={color} />
          <polyline points="10,30 50,50 90,30" fill="none" stroke="#fff" strokeWidth="3" opacity="0.7" />
          <line x1="50" y1="50" x2="50" y2="90" stroke="#fff" strokeWidth="3" opacity="0.7" />
        </svg>
      </div>
      {/* Drifting secondary crate */}
      <div style={{ position: 'absolute', bottom: 70, left: 85, width: '42px', height: '42px', opacity: 0.16, pointerEvents: 'none', animation: 'bedrock-drift 6s ease-in-out infinite' }}>
        <svg viewBox="0 0 100 100">
          <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill={accent} />
        </svg>
      </div>
    </>
  )
}

const AnimatedSummaryCard: FC<SummaryCardProps> = ({ title, subtitle, icon, color, accent, count, weight, cft, variant }) => {
  return (
    <Col xs={12} md={6}>
      <Card
        className="card-flush h-100 border-0"
        style={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: '220px',
          background: `linear-gradient(135deg, ${hexToRgba(color, 0.08)} 0%, ${hexToRgba(accent, 0.08)} 100%)`,
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)'
          e.currentTarget.style.boxShadow = `0 12px 28px ${hexToRgba(color, 0.25)}`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = ''
        }}
      >
        <AnimatedBackground variant={variant} color={color} accent={accent} />

        <Card.Body className="p-6" style={{ position: 'relative', zIndex: 1 }}>
          <div className="d-flex align-items-center mb-5">
            <div
              className="symbol symbol-55px me-4"
              style={{
                backgroundColor: hexToRgba(color, 0.18),
                borderRadius: '14px'
              }}
            >
              <span className="symbol-label" style={{ backgroundColor: 'transparent', borderRadius: '14px' }}>
                <i className={`${icon} fs-2x`} style={{ color }} />
              </span>
            </div>
            <div>
              <h3 className="fw-bold text-gray-800 mb-0">{title}</h3>
              <span className="text-muted fs-7">{subtitle}</span>
            </div>
          </div>

          <Row className="g-3">
            <Col xs={4}>
              <div className="text-center p-3 rounded" style={{ backgroundColor: hexToRgba(color, 0.14) }}>
                <div className="fw-bold fs-2x" style={{ color }}>{count}</div>
                <div className="text-muted fs-8 fw-semibold text-uppercase">
                  {variant === 'sale' ? 'Sales' : 'Purchases'}
                </div>
              </div>
            </Col>
            <Col xs={4}>
              <div className="text-center p-3 rounded" style={{ backgroundColor: hexToRgba(accent, 0.14) }}>
                <div className="fw-bold fs-2x" style={{ color: accent }}>
                  {weight >= 1000 ? (weight / 1000).toFixed(2) : weight.toFixed(1)}
                </div>
                <div className="text-muted fs-8 fw-semibold text-uppercase">Ton</div>
              </div>
            </Col>
            <Col xs={4}>
              <div className="text-center p-3 rounded" style={{ backgroundColor: hexToRgba(color, 0.08) }}>
                <div className="fw-bold fs-2x text-gray-700">{cft.toFixed(1)}</div>
                <div className="text-muted fs-8 fw-semibold text-uppercase">Cft</div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  )
}

// ============================================
// TODAY'S STATISTICS WIDGET
// ============================================

const TodayStatsWidget: FC = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<TodayStats>({
    salesCount: 0,
    purchasesCount: 0,
    salesTotalWeight: 0,
    salesTotalCft: 0,
    purchasesTotalWeight: 0,
    purchasesTotalCft: 0
  })

  useEffect(() => {
    fetchTodayStats()
    const interval = setInterval(fetchTodayStats, 60000)
    return () => clearInterval(interval)
  }, [])

  const fetchTodayStats = async () => {
    try {
      const todayStart = moment().startOf('day')
      const todayEnd = moment().endOf('day')

      const [salesResponse, purchasesResponse] = await Promise.all([
        genericGetAll(ENTITY_ID_CUSTOMER_SALE, {
          currentIndex: 0,
          sizePerPage: 1000,
          sortBy: 'salesSecondStepDate',
          sortOrder: 'desc'
        }),
        genericGetAll(ENTITY_ID_RAW_PURCHASE, {
          currentIndex: 0,
          sizePerPage: 1000,
          sortBy: 'purchaseDate',
          sortOrder: 'desc'
        })
      ])

      // Filter completed sales for today
      const allSales: Sale[] = salesResponse.data?.entities || []
      const sales = allSales.filter((sale) => {
        if (sale.isPartialRecord === true) return false
        if (!sale.salesSecondStepDate) return false
        const saleDate = moment(sale.salesSecondStepDate)
        return saleDate.isBetween(todayStart, todayEnd, undefined, '[]')
      })

      let salesTotalWeight = 0
      let salesTotalCft = 0
      sales.forEach((sale) => {
        if (sale.itemsList) {
          sale.itemsList.forEach((item) => {
            const qty = item.quantity || 0
            if (item.unitSymbol && item.unitSymbol.toLowerCase() === 'cft') {
              salesTotalCft += qty
            } else {
              salesTotalWeight += qty
            }
          })
        } else if (sale.netWeight) {
          salesTotalWeight += sale.netWeight
        } else if (sale.loadedWeight && sale.emptyWeight) {
          salesTotalWeight += (sale.loadedWeight - sale.emptyWeight)
        }
      })

      // Filter purchases for today
      const allPurchases: any[] = purchasesResponse.data?.entities || []
      const purchases = allPurchases.filter((purchase: any) => {
        if (!purchase.purchaseDate) return false
        const purchaseDate = moment(purchase.purchaseDate)
        return purchaseDate.isBetween(todayStart, todayEnd, undefined, '[]')
      })

      let purchasesTotalWeight = 0
      let purchasesTotalCft = 0
      purchases.forEach((purchase: any) => {
        if (purchase.itemsList) {
          purchase.itemsList.forEach((item: any) => {
            const qty = item.quantity || 0
            if (item.unitSymbol && item.unitSymbol.toLowerCase() === 'cft') {
              purchasesTotalCft += qty
            } else {
              purchasesTotalWeight += qty
            }
          })
        } else if (purchase.loadedWeight && purchase.emptyWeight) {
          purchasesTotalWeight += (purchase.loadedWeight - purchase.emptyWeight)
        }
      })

      setStats({
        salesCount: sales.length,
        purchasesCount: purchases.length,
        salesTotalWeight: salesTotalWeight,
        salesTotalCft: salesTotalCft,
        purchasesTotalWeight: purchasesTotalWeight,
        purchasesTotalCft: purchasesTotalCft
      })
    } catch (err) {
      console.error('Error fetching today stats:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card className="mb-5">
        <Card.Body className="text-center py-10">
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className="mb-5">
      <style>{ANIMATION_STYLES}</style>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-gray-800 mb-0">Today's Summary</h3>
          <span className="text-muted fs-7">{moment().format('DD MMMM YYYY')}</span>
        </div>
      </div>
      <Row className="g-5">
        <AnimatedSummaryCard
          variant="sale"
          title="Sale Today"
          subtitle="Completed sales for today"
          icon="fas fa-shopping-cart"
          color="#3E97FF"
          accent="#50CD89"
          count={stats.salesCount}
          weight={stats.salesTotalWeight}
          cft={stats.salesTotalCft}
        />
        <AnimatedSummaryCard
          variant="purchase"
          title="Purchase Today"
          subtitle="Completed purchases for today"
          icon="fas fa-boxes"
          color="#7239EA"
          accent="#FFC700"
          count={stats.purchasesCount}
          weight={stats.purchasesTotalWeight}
          cft={stats.purchasesTotalCft}
        />
      </Row>
    </div>
  )
}

// ============================================
// RECENT SALES WIDGET
// ============================================

const RecentSalesWidget: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [recentSales, setRecentSales] = useState<Sale[]>([])

  useEffect(() => {
    fetchRecentSales()
    const interval = setInterval(fetchRecentSales, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchRecentSales = async () => {
    try {
      const response = await genericGetAll(ENTITY_ID_CUSTOMER_SALE, {
        currentIndex: 0,
        sizePerPage: 50,
        sortBy: 'salesSecondStepDate',
        sortOrder: 'desc'
      })
      // Filter client-side for completed sales only (not partial records)
      const allSales: Sale[] = response.data?.entities || []
      const completed = allSales.filter((sale) => sale.isPartialRecord !== true)
      setRecentSales(completed.slice(0, 5)) // Take only top 5
    } catch (err) {
      console.error('Error fetching recent sales:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return formatDateTime(dateString)
  }

  const getNetWeight = (sale: Sale): number | null => {
    if (sale.netWeight) return sale.netWeight
    if (sale.loadedWeight && sale.emptyWeight) {
      return sale.loadedWeight - sale.emptyWeight
    }
    return null
  }

  const formatWeight = (weight: number | null) => {
    if (weight === null || weight === undefined) return '-'
    if (weight >= 1000) {
      return `${(weight / 1000).toFixed(2)} T`
    }
    return `${weight.toFixed(1)} Ton`
  }

  const getProductName = (sale: Sale) => {
    if (!sale.itemsList || sale.itemsList.length === 0) return '-'
    return sale.itemsList[0]?.inventoryItemName || '-'
  }

  return (
    <Card className="h-100" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* SVG Background - Receipt/document pattern */}
      <div style={{ position: 'absolute', top: -20, right: -20, width: '180px', height: '180px', opacity: 0.05, pointerEvents: 'none' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <rect x="40" y="20" width="120" height="160" rx="8" fill="none" stroke="#009EF7" strokeWidth="4" />
          <line x1="60" y1="50" x2="140" y2="50" stroke="#009EF7" strokeWidth="3" />
          <line x1="60" y1="75" x2="130" y2="75" stroke="#009EF7" strokeWidth="3" />
          <line x1="60" y1="100" x2="140" y2="100" stroke="#009EF7" strokeWidth="3" />
          <line x1="60" y1="125" x2="120" y2="125" stroke="#009EF7" strokeWidth="3" />
          <line x1="60" y1="150" x2="140" y2="150" stroke="#009EF7" strokeWidth="3" />
          <circle cx="160" cy="40" r="25" fill="#50CD89" opacity="0.5" />
          <path d="M150,40 L158,48 L175,30" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ position: 'absolute', bottom: -30, left: -30, width: '100px', height: '100px', opacity: 0.04, pointerEvents: 'none' }}>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="10" width="80" height="80" rx="8" fill="#009EF7" />
        </svg>
      </div>

      <Card.Header className="border-0 pt-5" style={{ position: 'relative', zIndex: 1 }}>
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold text-gray-800">Recent Sales</span>
          <span className="text-muted mt-1 fw-semibold fs-7">Latest completed transactions</span>
        </h3>
        <div className="card-toolbar">
          <Button
            variant="light-primary"
            size="sm"
            onClick={() => navigate('/sales-list')}
          >
            View All
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="pt-2" style={{ position: 'relative', zIndex: 1 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        ) : recentSales.length === 0 ? (
          <div className="text-center py-8">
            <div className="symbol symbol-60px mb-4">
              <span className="symbol-label bg-light-secondary">
                <i className="fas fa-file-alt fs-2x text-secondary"></i>
              </span>
            </div>
            <p className="text-muted mb-0">No sales yet</p>
            <small className="text-muted">Completed sales will appear here</small>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle gs-0 gy-3 mb-0">
              <thead>
                <tr className="text-muted fw-bold fs-7 text-uppercase">
                  <th>Ticket #</th>
                  <th>Vehicle</th>
                  <th>Product</th>
                  <th>Net Weight</th>
                  <th>Time</th>
                  <th className="text-center">Sync</th>
                </tr>
              </thead>
              <tbody>
                {recentSales.map((sale) => (
                  <tr
                    key={sale.id}
                    style={{cursor: 'pointer'}}
                    onClick={() => navigate(`/sale-new/view/${sale.id}`)}
                  >
                    <td>
                      <span className="fw-bold text-primary">
                        #{sale.ticketSequenceNumber || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-800 fw-semibold">
                        {sale.vehicleNumber || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-700">
                        {getProductName(sale)}
                      </span>
                    </td>
                    <td>
                      <Badge bg="success">
                        {formatWeight(getNetWeight(sale))}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted fs-8">
                        {formatDate(sale.salesSecondStepDate)}
                      </span>
                    </td>
                    <td className="text-center">
                      {sale.synced ? (
                        <i className="fas fa-check-circle text-success" title="Synced"></i>
                      ) : (
                        <i className="fas fa-exclamation-circle text-danger" title="Not Synced"></i>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ============================================
// SYNC STATUS WIDGET
// ============================================

const SyncStatusWidget: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [syncData, setSyncData] = useState<SyncData | null>(null)

  useEffect(() => {
    fetchSyncStatus()
    const interval = setInterval(fetchSyncStatus, 60000)
    return () => clearInterval(interval)
  }, [])

  const fetchSyncStatus = async () => {
    try {
      const response = await getAllSyncStatus()
      setSyncData(response.data)
    } catch (err) {
      console.error('Error fetching sync status:', err)
    } finally {
      setLoading(false)
    }
  }

  const getSyncStatusColor = () => {
    if (!syncData) return 'secondary'
    const pendingCount = syncData.totalPendingCount || 0
    if (pendingCount === 0) return 'success'
    return 'danger' // Always red if there are pending items
  }

  const getSyncStatusText = () => {
    if (!syncData) return 'Unknown'
    const pendingCount = syncData.totalPendingCount || 0
    if (pendingCount === 0) return 'All Synced'
    return `${pendingCount} Pending`
  }

  const statusColor = getSyncStatusColor()

  return (
    <Card style={{ position: 'relative', overflow: 'hidden' }}>
      {/* SVG Background - Cloud sync pattern */}
      <div style={{ position: 'absolute', top: -20, right: 220, width: '160px', height: '160px', opacity: 0.06, pointerEvents: 'none' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M160,120 C180,120 190,100 180,85 C190,60 170,45 150,55 C140,35 110,35 100,55 C80,45 55,55 55,80 C35,80 30,110 50,120 Z" fill="#50CD89" />
          <path d="M90,95 L90,130 M90,130 L75,115 M90,130 L105,115" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" />
          <path d="M130,125 L130,90 M130,90 L115,105 M130,90 L145,105" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ position: 'absolute', bottom: -20, left: -20, width: '90px', height: '90px', opacity: 0.04, pointerEvents: 'none' }}>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#50CD89" strokeWidth="8" strokeDasharray="20 10" />
        </svg>
      </div>

      <Card.Body className="p-4" style={{ position: 'relative', zIndex: 1 }}>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        ) : (
          <Row className="align-items-center g-4">
            {/* Left: title + status */}
            <Col xs={12} md={4} lg={3}>
              <div className="d-flex align-items-center">
                <div className="symbol symbol-50px me-3">
                  <span className={`symbol-label bg-light-${statusColor}`}>
                    <i className={`fas fa-cloud fs-2x text-${statusColor}`}></i>
                  </span>
                </div>
                <div>
                  <h3 className="fw-bold text-gray-800 mb-1">Sync Status</h3>
                  <Badge bg={statusColor} className="fs-8 fw-bold">
                    {getSyncStatusText()}
                  </Badge>
                </div>
              </div>
            </Col>

            {/* Middle: stat tiles */}
            <Col xs={12} md={6} lg={7}>
              <Row className="g-3">
                <Col xs={4}>
                  <div className="bg-light-success rounded p-3 text-center">
                    <div className="text-success fs-2 fw-bold lh-1">{syncData?.totalRecordCount || 0}</div>
                    <div className="text-muted fs-8 fw-semibold text-uppercase mt-1">Total Records</div>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="bg-light-danger rounded p-3 text-center">
                    <div className="text-danger fs-2 fw-bold lh-1">{syncData?.totalPendingCount || 0}</div>
                    <div className="text-muted fs-8 fw-semibold text-uppercase mt-1">Pending</div>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="bg-light-info rounded p-3 text-center">
                    <div className="text-info fs-2 fw-bold lh-1">{syncData?.entityCount || 0}</div>
                    <div className="text-muted fs-8 fw-semibold text-uppercase mt-1">Collections</div>
                  </div>
                </Col>
              </Row>
            </Col>

            {/* Right: manage button */}
            <Col xs={12} md={2} lg={2} className="text-md-end">
              <Button
                variant="light-primary"
                onClick={() => navigate('/sync')}
                className="w-100 w-md-auto"
              >
                <i className="fas fa-cog me-2"></i>
                Manage
              </Button>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  )
}

// ============================================
// PENDING SALES WIDGET
// ============================================

const PendingSalesWidget: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [pendingSales, setPendingSales] = useState<Sale[]>([])

  useEffect(() => {
    fetchPendingSales()
    const interval = setInterval(fetchPendingSales, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchPendingSales = async () => {
    try {
      // Pending 2-step sales have no weigh-out yet, so no salesSecondStepDate (the
      // sale date) - the weigh-in stamp is the only date they have. Sort and the
      // rendered date below deliberately stay on salesFirstStepDate here.
      const response = await genericGetAll(ENTITY_ID_CUSTOMER_SALE, {
        currentIndex: 0,
        sizePerPage: 100,
        sortBy: 'salesFirstStepDate',
        sortOrder: 'desc'
      })
      // Filter client-side for partial records (pending 2-step sales)
      const allSales: Sale[] = response.data?.entities || []
      const pending = allSales.filter((sale) => sale.isPartialRecord === true)
      setPendingSales(pending)
    } catch (err) {
      console.error('Error fetching pending sales:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return formatDateTime(dateString)
  }

  const completeSale = (sale: Sale) => {
    navigate(`/sale-new/edit/${sale.id}`)
  }

  return (
    <Card className="h-100" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* SVG Background - Hourglass/waiting pattern */}
      <div style={{ position: 'absolute', top: -15, right: -15, width: '170px', height: '170px', opacity: 0.05, pointerEvents: 'none' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <rect x="60" y="30" width="80" height="15" rx="4" fill="#FFC700" />
          <rect x="60" y="155" width="80" height="15" rx="4" fill="#FFC700" />
          <path d="M70,45 L70,80 L100,110 L130,80 L130,45" fill="none" stroke="#FFC700" strokeWidth="6" />
          <path d="M70,155 L70,120 L100,90 L130,120 L130,155" fill="none" stroke="#FFC700" strokeWidth="6" />
          <ellipse cx="100" cy="135" rx="20" ry="8" fill="#FFC700" opacity="0.5" />
        </svg>
      </div>
      <div style={{ position: 'absolute', bottom: -25, left: -25, width: '100px', height: '100px', opacity: 0.04, pointerEvents: 'none' }}>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="35" fill="none" stroke="#FFC700" strokeWidth="6" />
          <path d="M50,25 L50,50 L70,60" fill="none" stroke="#FFC700" strokeWidth="5" strokeLinecap="round" />
        </svg>
      </div>

      <Card.Header className="border-0 pt-5" style={{ position: 'relative', zIndex: 1 }}>
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold text-gray-800">
            Pending Sales
            {pendingSales.length > 0 && (
              <Badge bg="warning" className="ms-2">{pendingSales.length}</Badge>
            )}
          </span>
          <span className="text-muted mt-1 fw-semibold fs-7">Vehicles awaiting completion</span>
        </h3>
        <div className="card-toolbar">
          <Button
            variant="light-primary"
            size="sm"
            onClick={() => navigate('/pending-sales')}
          >
            View All
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="pt-2" style={{ position: 'relative', zIndex: 1 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        ) : pendingSales.length === 0 ? (
          <div className="text-center py-8">
            <div className="symbol symbol-60px mb-4">
              <span className="symbol-label bg-light-success">
                <i className="fas fa-check-circle fs-2x text-success"></i>
              </span>
            </div>
            <p className="text-muted mb-0">No pending sales</p>
            <small className="text-muted">All vehicles have been processed</small>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle gs-0 gy-3 mb-0">
              <thead>
                <tr className="text-muted fw-bold fs-7 text-uppercase">
                  <th>Vehicle</th>
                  <th>Customer</th>
                  <th>Weight</th>
                  <th>Inventory</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingSales.slice(0, 5).map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="symbol symbol-35px me-3">
                          <span className="symbol-label bg-light-warning">
                            <i className="fas fa-truck fs-6 text-warning"></i>
                          </span>
                        </div>
                        <div>
                          <span className="fw-bold text-gray-800 d-block">
                            {sale.vehicleNumber || 'N/A'}
                          </span>
                          <span className="text-muted fs-8">{formatDate(sale.salesFirstStepDate)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-gray-700 fs-7">
                        {sale.salesCustomer?.name || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-700 fs-7">
                        {sale.emptyWeight ? `${sale.emptyWeight} Ton` : '-'}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-700 fs-7">
                        {sale.itemsList?.map((item) => item.inventoryItemName).filter(Boolean).join(', ') || '-'}
                      </span>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => completeSale(sale)}
                      >
                        <i className="fas fa-check me-1"></i>
                        Complete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {pendingSales.length > 5 && (
              <div className="text-center pt-3 border-top">
                <Button
                  variant="link"
                  className="text-primary"
                  onClick={() => navigate('/pending-sales')}
                >
                  +{pendingSales.length - 5} more pending sales
                </Button>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ============================================
// PENDING PURCHASES WIDGET
// ============================================

const PendingPurchasesWidget: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [pendingPurchases, setPendingPurchases] = useState<Purchase[]>([])

  useEffect(() => {
    fetchPendingPurchases()
    const interval = setInterval(fetchPendingPurchases, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchPendingPurchases = async () => {
    try {
      const response = await genericGetAll(ENTITY_ID_RAW_PURCHASE, {
        currentIndex: 0,
        sizePerPage: 100,
        sortBy: 'purchaseFirstStepDate',
        sortOrder: 'desc'
      })
      const allPurchases: Purchase[] = response.data?.entities || []
      // QR drafts are listed on Draft Purchases, not here
      const pending = allPurchases.filter((purchase) => purchase.isPartialRecord === true && purchase.source !== 'QR_AUTO')
      setPendingPurchases(pending)
    } catch (err) {
      console.error('Error fetching pending purchases:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return formatDateTime(dateString)
  }

  const completePurchase = (purchase: Purchase) => {
    navigate(`/purchase-new/edit/${purchase.id}`)
  }

  return (
    <Card className="h-100" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* SVG Background - Crate/box pattern (purchases) */}
      <div style={{ position: 'absolute', top: -15, right: -15, width: '170px', height: '170px', opacity: 0.05, pointerEvents: 'none' }}>
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <polygon points="100,30 160,60 160,140 100,170 40,140 40,60" fill="none" stroke="#7239EA" strokeWidth="6" />
          <polyline points="40,60 100,90 160,60" fill="none" stroke="#7239EA" strokeWidth="6" />
          <line x1="100" y1="90" x2="100" y2="170" stroke="#7239EA" strokeWidth="6" />
          <circle cx="100" cy="50" r="8" fill="#7239EA" opacity="0.6" />
        </svg>
      </div>
      <div style={{ position: 'absolute', bottom: -25, left: -25, width: '100px', height: '100px', opacity: 0.04, pointerEvents: 'none' }}>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="20" width="60" height="60" rx="6" fill="none" stroke="#7239EA" strokeWidth="6" />
          <line x1="20" y1="50" x2="80" y2="50" stroke="#7239EA" strokeWidth="6" />
          <line x1="50" y1="20" x2="50" y2="80" stroke="#7239EA" strokeWidth="6" />
        </svg>
      </div>

      <Card.Header className="border-0 pt-5" style={{ position: 'relative', zIndex: 1 }}>
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold text-gray-800">
            Pending Purchases
            {pendingPurchases.length > 0 && (
              <Badge bg="warning" className="ms-2">{pendingPurchases.length}</Badge>
            )}
          </span>
          <span className="text-muted mt-1 fw-semibold fs-7">Loaded trucks awaiting completion</span>
        </h3>
        <div className="card-toolbar">
          <Button
            variant="light-primary"
            size="sm"
            onClick={() => navigate('/pending-purchases')}
          >
            View All
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="pt-2" style={{ position: 'relative', zIndex: 1 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        ) : pendingPurchases.length === 0 ? (
          <div className="text-center py-8">
            <div className="symbol symbol-60px mb-4">
              <span className="symbol-label bg-light-success">
                <i className="fas fa-check-circle fs-2x text-success"></i>
              </span>
            </div>
            <p className="text-muted mb-0">No pending purchases</p>
            <small className="text-muted">All trucks have been processed</small>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle gs-0 gy-3 mb-0">
              <thead>
                <tr className="text-muted fw-bold fs-7 text-uppercase">
                  <th>Vehicle</th>
                  <th>Supplier</th>
                  <th>Loaded Wt.</th>
                  <th>Product</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingPurchases.slice(0, 5).map((purchase) => (
                  <tr key={purchase.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="symbol symbol-35px me-3">
                          <span className="symbol-label bg-light-primary">
                            <i className="fas fa-truck fs-6 text-primary"></i>
                          </span>
                        </div>
                        <div>
                          <span className="fw-bold text-gray-800 d-block">
                            {purchase.vehicleNumber || 'N/A'}
                          </span>
                          <span className="text-muted fs-8">{formatDate(purchase.purchaseFirstStepDate)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-gray-700 fs-7">
                        {purchase.supplier?.name || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-700 fs-7">
                        {purchase.loadedWeight ? `${purchase.loadedWeight} Ton` : '-'}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-700 fs-7">
                        {purchase.itemsList?.map((item) => item.inventoryItemName).filter(Boolean).join(', ') || '-'}
                      </span>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => completePurchase(purchase)}
                      >
                        <i className="fas fa-check me-1"></i>
                        Complete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {pendingPurchases.length > 5 && (
              <div className="text-center pt-3 border-top">
                <Button
                  variant="link"
                  className="text-primary"
                  onClick={() => navigate('/pending-purchases')}
                >
                  +{pendingPurchases.length - 5} more pending purchases
                </Button>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ============================================
// MAIN DASHBOARD PAGE
// ============================================

const DashboardPage: FC = () => {
  const navigate = useNavigate()
  const [showVehicleDialog, setShowVehicleDialog] = useState(false)

  const handleVehicleDialogClose = () => {
    setShowVehicleDialog(false)
  }

  return (
    <>
      {/* Live Clock */}
      <LiveClock />

      {/* Today's Statistics */}
      <TodayStatsWidget />

      {/* Quick Actions Section */}
      <div className="mb-5 mb-xl-8">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <h2 className="text-gray-800 fw-bold mb-0">Quick Actions</h2>
          <HardwareCheckButton />
        </div>
        <Row className="g-5">
          <Col xs={12} md={6} xl={4}>
            <QuickActionButton
              title="New Vehicle"
              subtitle="Register before weighing"
              description={[
                "Add vehicle number & details",
                "Set default tare weight",
                "Required for new trucks"
              ]}
              icon="fas fa-truck"
              color="info"
              onClick={() => setShowVehicleDialog(true)}
            />
          </Col>
          <Col xs={12} md={6} xl={4}>
            <QuickActionGroupCard
              title="New Sale"
              subtitle="Customer sale entry"
              icon="fas fa-shopping-cart"
              color="success"
              subActions={[
                {
                  label: '1 Step',
                  hint: 'Tare known — single weighing',
                  icon: 'fas fa-bolt',
                  onClick: () => navigate('/sale-new'),
                },
                {
                  label: '2 Step',
                  hint: 'Empty in → load → weigh out',
                  icon: 'fas fa-balance-scale',
                  onClick: () => navigate('/sale-step1'),
                },
              ]}
            />
          </Col>
          <Col xs={12} md={6} xl={4}>
            <QuickActionGroupCard
              title="New Purchase"
              subtitle="Supplier purchase entry"
              icon="fas fa-boxes"
              color="primary"
              subActions={[
                {
                  label: '1 Step',
                  hint: 'Direct entry — supplier & items',
                  icon: 'fas fa-bolt',
                  onClick: () => navigate('/purchase-new'),
                },
                {
                  label: '2 Step',
                  hint: 'Loaded in → unload → weigh empty',
                  icon: 'fas fa-balance-scale-right',
                  onClick: () => navigate('/purchase-step1'),
                },
              ]}
            />
          </Col>
        </Row>
      </div>

      {/* Pending Transactions Section */}
      <Row className="g-5 mb-5">
        <Col xl={6}>
          <PendingSalesWidget />
        </Col>
        <Col xl={6}>
          <PendingPurchasesWidget />
        </Col>
      </Row>

      {/* Sync Status */}
      <Row className="g-5 mb-5 mb-xl-8">
        <Col xs={12}>
          <SyncStatusWidget />
        </Col>
      </Row>

      {/* Vehicle Add Dialog */}
      <VehicleAddEditDialog
        show={showVehicleDialog}
        handleClose={handleVehicleDialogClose}
        id={null}
      />
    </>
  )
}

// ============================================
// DASHBOARD WRAPPER
// ============================================

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  const myData = useContext(BedRockContext)

  useEffect(() => {
    myData.setBaseData({
      toolBarButtons: []
    })
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
      <DashboardPage />
    </>
  )
}

export {DashboardWrapper}
